import { FC, useEffect, useMemo, useState } from 'react';
import { fetchOrders, OrderResponse } from '../../api/orders';
import { fetchBays, Bay } from '../../api/bays';
import { fetchUsers, PaginatedUsersResponse } from '../../api/users';
import { UserProfile, fetchCurrentUser } from '../../api/auth';
import BayView from './components/BayView';
import TechnicianView from './components/TechnicianView';
import VehicleTimelineView from './components/VehicleTimelineView';


type ViewType = 'bay' | 'technician' | 'car';

const SchedulerPage: FC = () => {
    const [view, setView] = useState<ViewType>('bay');
    const [orders, setOrders] = useState<OrderResponse[]>([]);
    const [bays, setBays] = useState<Bay[]>([]);
    const [technicians, setTechnicians] = useState<UserProfile[]>([]);
    const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [ordersRes, baysRes, usersRes, meRes] = await Promise.all([
                fetchOrders({ limit: 100 }), // Increased limit for calendar visibility
                fetchBays({ limit: 100, status: 'active' }),
                fetchUsers({ limit: 100, role: 'TECHNICIAN' }),
                fetchCurrentUser(),
            ]);

            setOrders(ordersRes.data);
            setBays(baysRes.data);
            setTechnicians(usersRes.data);
            setCurrentUser(meRes.user);
        } catch (err) {
            setError('Failed to load scheduler data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [selectedDate]);

    const viewLabel = useMemo(() => {
        switch (view) {
            case 'bay': return 'By Bay';
            case 'technician': return 'By Technician';
            case 'car': return 'By Car';
            default: return '';
        }
    }, [view]);

    const { startHour, endHour } = useMemo(() => {
        // Standardize to 10 AM - 6 PM as per user request
        return { startHour: 10, endHour: 18 };
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-semibold text-slate-800">
                        Schedule SO - <span className="text-primary">{viewLabel}</span>
                    </h2>
                </div>

                <div className="flex items-center gap-6 rounded-xl bg-white px-4 py-2 shadow-sm border border-slate-200">
                    <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium text-slate-500">Switch View:</span>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="view"
                                checked={view === 'car'}
                                onChange={() => setView('car')}
                                className="text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className={view === 'car' ? 'text-primary font-semibold' : 'text-slate-600'}>By Car</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="view"
                                checked={view === 'bay'}
                                onChange={() => setView('bay')}
                                className="text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className={view === 'bay' ? 'text-primary font-semibold' : 'text-slate-600'}>By Bay</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="view"
                                checked={view === 'technician'}
                                onChange={() => setView('technician')}
                                className="text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className={view === 'technician' ? 'text-primary font-semibold' : 'text-slate-600'}>By Technician</span>
                        </label>
                    </div>

                    <div className="h-6 w-px bg-slate-200" />

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSelectedDate(new Date())}
                            className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        >
                            Today
                        </button>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => {
                                    const d = new Date(selectedDate);
                                    d.setDate(d.getDate() - 1);
                                    setSelectedDate(d);
                                }}
                                className="p-1 hover:text-primary transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <span className="text-sm font-semibold text-slate-700 mx-1">
                                {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </span>
                            <button
                                onClick={() => {
                                    const d = new Date(selectedDate);
                                    d.setDate(d.getDate() + 1);
                                    setSelectedDate(d);
                                }}
                                className="p-1 hover:text-primary transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {error ? (
                <div className="rounded-xl border border-danger/40 bg-danger/5 px-4 py-3 text-sm text-danger">{error}</div>
            ) : null}

            <div className="rounded-3xl bg-white shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-500">
                        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                        Loading schedule...
                    </div>
                ) : (
                    <>
                        {view === 'bay' && <BayView orders={orders} bays={bays} selectedDate={selectedDate} technicians={technicians} startHour={startHour} endHour={endHour} onRefresh={loadData} />}
                        {view === 'technician' && <TechnicianView orders={orders} technicians={technicians} selectedDate={selectedDate} bays={bays} startHour={startHour} endHour={endHour} onRefresh={loadData} />}
                        {view === 'car' && <VehicleTimelineView orders={orders} selectedDate={selectedDate} bays={bays} technicians={technicians} startHour={startHour} endHour={endHour} onRefresh={loadData} />}

                    </>
                )}
            </div>
        </div>
    );
};

export default SchedulerPage;
